// SCR-STAFF-313 — فهرست و ویرایشگر مرحله‌ای قانون پذیرش (US-SCH-110، 112 تا 118).
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type AdmissionRuleView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useBookingOptions } from './common';

export const SCR_STAFF_313 = 'SCR-STAFF-313';

const TYPES = [
  'Age',
  'Gender',
  'PatientTag',
  'OtherProviderAppointment',
  'OpenAppointmentLimit',
  'DailyAppointmentLimit',
  'NoShowBlacklist',
  'PatientTypeQuota',
] as const;

const MODES = ['Warn', 'Block'] as const;
const STATUSES = ['Active', 'Inactive'] as const;
const CHANNELS = ['Staff', 'Online', 'Kiosk'] as const;
const PROVIDER_TYPES = ['Physician', 'Dentist', 'Therapist', 'Technician', 'Nurse', 'Psychologist', 'Nutritionist', 'Midwife'] as const;

interface Draft {
  name: string;
  type: (typeof TYPES)[number];
  mode: (typeof MODES)[number];
  priority: string;
  channels: string;
  branchId: string;
  departmentIds: string[];
  providerIds: string[];
  providerTypes: string[];
  parametersJson: string;
  message: string;
  status: (typeof STATUSES)[number];
}

const EMPTY: Draft = {
  name: '',
  type: 'Age',
  mode: 'Block',
  priority: '100',
  channels: 'Staff,Online,Kiosk',
  branchId: '',
  departmentIds: [],
  providerIds: [],
  providerTypes: [],
  parametersJson: '{"operator":"GreaterOrEqual","unit":"Months","value":216}',
  message: '',
  status: 'Active',
};

function parametersOf(type: Draft['type']): string {
  switch (type) {
    case 'Age':
      return '{"operator":"GreaterOrEqual","unit":"Months","value":216}';
    case 'Gender':
      return '{"gender":"Female"}';
    case 'PatientTag':
      return '{"mode":"RequireAny","codes":["vip"]}';
    case 'OtherProviderAppointment':
      return '{"days":7,"exemptProviderIds":[]}';
    case 'OpenAppointmentLimit':
    case 'DailyAppointmentLimit':
      return '{"max":1}';
    case 'NoShowBlacklist':
      return '{"threshold":2,"windowMonths":6,"blockDays":30}';
    case 'PatientTypeQuota':
      return '{"newMax":0,"returningMax":null}';
    default:
      return '{}';
  }
}

function toggle(values: string[], value: string, checked: boolean): string[] {
  return checked ? [...values, value] : values.filter((item) => item !== value);
}

export function AdmissionRulesPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('sch.admission_rule.manage');
  const idempotency = useIdempotencyKey();
  const queryClient = useQueryClient();
  const key = ['sch', session?.tenantId, 'admission-rules'] as const;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [step, setStep] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const rules = useQuery({
    queryKey: [...key, typeFilter, statusFilter],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/admission-rules', {
          params: { query: { limit: 50, type: typeFilter || undefined, status: statusFilter || undefined } },
        }),
      ),
  });
  const providers = useBookingOptions(draft?.branchId || null, null, draft !== null);
  const departments = useQuery({
    queryKey: ['org', session?.tenantId, 'departments', draft?.branchId],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/departments', {
          params: { query: { branchId: draft?.branchId || undefined } },
        }),
      ),
    enabled: draft !== null && Boolean(draft.branchId) && access.can('org.resource.view'),
  });
  const save = useMutation({
    mutationFn: (body: Draft) =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/admission-rules', {
          body: {
            name: body.name.trim(),
            branchId: body.branchId || null,
            departmentIds: body.departmentIds,
            providerIds: body.providerIds,
            providerTypes: body.providerTypes,
            type: body.type,
            parametersJson: body.parametersJson,
            mode: body.mode,
            priority: Number(body.priority),
            channels: body.channels.split(',').map((item) => item.trim()).filter(Boolean),
            message: body.message.trim() || null,
            status: body.status,
          },
          params: { header: { 'Idempotency-Key': idempotency.current() } },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      setDraft(null);
      setStep(0);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.DELETE('/api/v1/scheduling/admission-rules/{id}', { params: { path: { id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const columns: DataTableColumn<AdmissionRuleView>[] = [
    { id: 'name', headerKey: 'sch:admission.columns.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'sch:admission.columns.type', cell: (row) => t(`sch:admission.type.${row.type}`) },
    { id: 'mode', headerKey: 'sch:admission.columns.mode', cell: (row) => t(`sch:admission.mode.${row.mode}`) },
    { id: 'priority', headerKey: 'sch:admission.columns.priority', cell: (row) => String(row.priority) },
    { id: 'status', headerKey: 'sch:admission.columns.status', cell: (row) => t(`sch:admission.status.${row.status}`) },
    {
      id: 'actions',
      headerKey: 'sch:admission.columns.actions',
      cell: (row) =>
        write ? (
          <Button size="small" color="error" onClick={() => remove.mutate(row.id)} data-testid="admission-delete">
            {t('common:actions.delete')}
          </Button>
        ) : null,
    },
  ];

  const branchName = activeTenant?.branches.find((branch) => branch.branchId === draft?.branchId)?.name;
  const selectedProviders = (providers.data?.providers ?? []).filter((provider) => draft?.providerIds?.includes(provider.id) === true);
  const preview = !draft
    ? ''
    : draft.providerIds.length > 0
      ? t('sch:admission.preview.providers', {
          names: (selectedProviders.length > 0 ? selectedProviders.map((provider) => provider.name) : draft.providerIds).join(
            t('sch:admission.preview.separator'),
          ),
        })
      : draft.providerTypes.length > 0
        ? t('sch:admission.preview.types', {
            types: draft.providerTypes.map((type) => t(`sch:admission.provider_type.${type}`)).join(t('sch:admission.preview.separator')),
          })
        : draft.branchId
          ? t('sch:admission.preview.branch', { branch: branchName ?? draft.branchId })
          : t('sch:admission.preview.all');

  return (
    <ListPage
      screenId={SCR_STAFF_313}
      titleKey="sch:admission.title"
      subtitleKey="sch:admission.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            select
            size="small"
            label={t('sch:admission.filter.type')}
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            inputProps={{ 'data-testid': 'admission-filter-type' }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('sch:admission.filter.all_types')}</MenuItem>
            {TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`sch:admission.type.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('sch:admission.filter.status')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            inputProps={{ 'data-testid': 'admission-filter-status' }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('sch:admission.filter.all_statuses')}</MenuItem>
            {STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {t(`sch:admission.status.${status}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
      actions={
        write ? (
          <Button
            variant="contained"
            data-testid="admission-new"
            onClick={() => {
              setDraft(EMPTY);
              setStep(0);
            }}
          >
            {t('sch:admission.new')}
          </Button>
        ) : undefined
      }
    >
      <DataTable
        captionKey="sch:admission.title"
        columns={columns}
        rows={rules.data?.items}
        getRowId={(row) => row.id}
        isLoading={rules.isPending}
        error={rules.error ?? remove.error}
        onRetry={() => void rules.refetch()}
        emptyTitleKey="sch:admission.empty"
      />
      <FormDialog
        open={draft !== null}
        titleKey="sch:admission.edit_title"
        screenId={SCR_STAFF_313}
        isDirty={draft !== null}
        isSubmitting={save.isPending}
        submitError={save.error}
        submitKey={step < 2 ? 'sch:admission.next' : 'common:actions.save'}
        onClose={() => {
          setDraft(null);
          setStep(0);
        }}
        onSubmit={() => {
          if (!draft) {
            return;
          }
          if (step < 2) {
            setStep(step + 1);
            return;
          }
          save.mutate(draft);
        }}
      >
        {draft ? (
          <Stack spacing={2} data-testid="admission-editor">
            <Stepper activeStep={step}>
              <Step>
                <StepLabel>{t('sch:admission.step.basics')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('sch:admission.step.condition')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('sch:admission.step.message')}</StepLabel>
              </Step>
            </Stepper>
            {step === 0 ? (
              <>
                <TextField
                  required
                  label={t('sch:admission.field.name')}
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  inputProps={{ 'data-testid': 'admission-name', maxLength: 100 }}
                />
                <TextField
                  select
                  label={t('sch:admission.field.branch')}
                  value={draft.branchId}
                  onChange={(event) => setDraft({ ...draft, branchId: event.target.value, departmentIds: [], providerIds: [] })}
                  inputProps={{ 'data-testid': 'admission-branch' }}
                >
                  <MenuItem value="">{t('sch:admission.field.all_branches')}</MenuItem>
                  {(activeTenant?.branches ?? []).map((branch) => (
                    <MenuItem key={branch.branchId} value={branch.branchId}>
                      {branch.name ?? branch.branchId}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary">
                  {draft.branchId ? t('sch:admission.scope.empty_means_all') : t('sch:admission.scope.pick_branch')}
                </Typography>
                <Typography variant="subtitle2">{t('sch:admission.field.provider_types')}</Typography>
                <Box>
                  {PROVIDER_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={draft.providerTypes.includes(type)}
                          onChange={(event) => setDraft({ ...draft, providerTypes: toggle(draft.providerTypes, type, event.target.checked) })}
                        />
                      }
                      label={t(`sch:admission.provider_type.${type}`)}
                    />
                  ))}
                </Box>
                {draft.branchId ? (
                  <>
                    <Typography variant="subtitle2">{t('sch:admission.field.providers')}</Typography>
                    <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                      {(providers.data?.providers ?? []).map((provider) => (
                        <FormControlLabel
                          key={provider.id}
                          control={
                            <Checkbox
                              checked={draft.providerIds.includes(provider.id)}
                              onChange={(event) => setDraft({ ...draft, providerIds: toggle(draft.providerIds, provider.id, event.target.checked) })}
                            />
                          }
                          label={provider.name}
                        />
                      ))}
                    </Box>
                    {access.can('org.resource.view') ? (
                      <>
                        <Typography variant="subtitle2">{t('sch:admission.field.departments')}</Typography>
                        <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                          {(departments.data?.items ?? []).map((department) => (
                            <FormControlLabel
                              key={department.id}
                              control={
                                <Checkbox
                                  checked={draft.departmentIds.includes(department.id)}
                                  onChange={(event) =>
                                    setDraft({ ...draft, departmentIds: toggle(draft.departmentIds, department.id, event.target.checked) })
                                  }
                                />
                              }
                              label={department.name}
                            />
                          ))}
                        </Box>
                      </>
                    ) : null}
                  </>
                ) : null}
                <TextField
                  select
                  label={t('sch:admission.field.type')}
                  value={draft.type}
                  onChange={(event) => {
                    const type = event.target.value as Draft['type'];
                    setDraft({ ...draft, type, parametersJson: parametersOf(type) });
                  }}
                  inputProps={{ 'data-testid': 'admission-type' }}
                >
                  {TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`sch:admission.type.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t('sch:admission.field.mode')}
                  value={draft.mode}
                  onChange={(event) => setDraft({ ...draft, mode: event.target.value as Draft['mode'] })}
                >
                  {MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {t(`sch:admission.mode.${mode}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t('sch:admission.field.priority')}
                  value={draft.priority}
                  onChange={(event) => setDraft({ ...draft, priority: event.target.value })}
                  inputProps={{ 'data-testid': 'admission-priority' }}
                />
                <Stack>
                  {CHANNELS.map((channel) => {
                    const selected = draft.channels.split(',').map((item) => item.trim()).filter(Boolean);
                    return (
                      <FormControlLabel
                        key={channel}
                        control={
                          <Checkbox
                            checked={selected.includes(channel)}
                            onChange={(event) => {
                              const next = event.target.checked ? [...selected, channel] : selected.filter((item) => item !== channel);
                              setDraft({ ...draft, channels: next.join(',') });
                            }}
                          />
                        }
                        label={t(`sch:admission.channel.${channel}`)}
                      />
                    );
                  })}
                </Stack>
              </>
            ) : null}
            {step === 1 ? (
              <TextField
                required
                multiline
                minRows={4}
                label={t('sch:admission.field.parameters')}
                value={draft.parametersJson}
                onChange={(event) => setDraft({ ...draft, parametersJson: event.target.value })}
                inputProps={{ 'data-testid': 'admission-parameters' }}
              />
            ) : null}
            {step === 2 ? (
              <>
                <TextField
                  select
                  label={t('sch:admission.field.status')}
                  value={draft.status}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value as Draft['status'] })}
                >
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`sch:admission.status.${status}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  multiline
                  minRows={2}
                  label={t('sch:admission.field.message')}
                  value={draft.message}
                  onChange={(event) => setDraft({ ...draft, message: event.target.value })}
                  inputProps={{ maxLength: 300, 'data-testid': 'admission-message' }}
                />
                <Typography data-testid="admission-preview">{preview}</Typography>
              </>
            ) : null}
          </Stack>
        ) : null}
      </FormDialog>
    </ListPage>
  );
}
